import React, { useEffect } from 'react'
import Dialog from '@mui/material/Dialog'
import { Controller, useForm } from 'react-hook-form'

import { Button } from '../../../components/UI'
import DropDown from '../../../components/Input/DropDown'
import FindTutorFilterItem from './FindTutorFilterItem'

import { useGetInfo, useMainCategories } from '../../../hooks'

import { compareTimeZones } from '../../../helpers'

import { suitableTimes } from '../../../constants'
import { tutorClassLevels, tutorPrices, tutorRatings } from './data'

const FindTutorFilterModal = ({ filters, setFilters, open, onClose }) => {
  const { control, watch, handleSubmit, setValue } = useForm({
    defaultValues: {
      class_banner_category: filters.class_banner_category || [],
      languages: filters.languages || '_',
      price: filters.price || '_',
      rating: filters.rating || '_',
      class_banner_level: filters.class_banner_level || '_',
      timezone: filters.timezone || '_',
      time_preferences: filters.time_preferences || [],
      country_of_origin: filters.country_of_origin || '_',
      country_of_residence: filters.country_of_residence || '_',
    },
  })

  const { formattedWorldTimezones, formattedWorldLanguages, formattedCountries } = useGetInfo()
  const categories = useMainCategories()

  const activeCategory = watch('class_banner_category')
  const price = watch('price')
  const rating = watch('rating')
  const timePreferences = watch('time_preferences')

  const formattedTimezones = [
    {
      value: '_',
      label: 'Any Timezone',
    },
    ...formattedWorldTimezones.sort(compareTimeZones),
  ]
  const formattedLanguages = [
    {
      value: '_',
      label: 'Any Language',
    },
    ...formattedWorldLanguages,
  ]
  const allFormattedCountries = [
    {
      value: '_',
      label: 'Any Country',
    },
    ...formattedCountries,
  ]

  // const handleChangeCategory = (value) => {
  //   if (value && value === activeCategory) {
  //     return setValue('class_banner_category', '')
  //   }

  //   setValue('class_banner_category', value)
  // }

  const handleSelectClassTypes = (value) => {
    const bannerCategory = () => {
      if (activeCategory.includes(value)) {
        return activeCategory.filter((item) => item !== value)
      }

      return [...activeCategory, value]
    }
    setValue('class_banner_category', bannerCategory())
  }

  const handleChangePrice = (value) => {
    setValue('price', value)
  }

  const handleChangeRating = (value) => {
    setValue('rating', value)
  }

  // const handleChangeTimePreferences = (value) => {
  //   if (value === timePreferences) {
  //     return setValue('time_preferences', '')
  //   }

  //   setValue('time_preferences', value)
  // }

  const handleSelectSuitableTimeSlot = async (value) => {
    const timepref = () => {
      if (timePreferences.includes(value)) {
        return timePreferences.filter((item) => item !== value)
      }

      return [...timePreferences, value]
    }
    setValue('time_preferences', timepref())
  }

  const handleFormSubmit = (data) => {
    setFilters({
      ...filters,
      ...data,
    })

    onClose()
  }

  const handleReset = () => {
    setValue('class_banner_category', filters.class_banner_category || '')
    setValue('languages', filters.languages || '_')
    setValue('price', filters.price || '_')
    setValue('rating', filters.rating || '_')
    setValue('class_banner_level', filters.class_banner_level || '_')
    setValue('timezone', filters.timezone || '_')
    setValue('time_preferences', filters.time_preferences || '')
    setValue('country_of_origin', filters.country_of_origin || '_')
    setValue('country_of_residence', filters.country_of_residence || '_')
  }

  const handleClose = () => {
    onClose()

    handleReset()
  }

  useEffect(() => {
    if (open) {
      handleReset()
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="body"
      fullWidth
      classes={{
        paper: 'md:!max-w-[720px] !p-[32px] !rounded-[10px] !bg-[#FEEBDD] !shadow-modal',
      }}
    >
      <form
        className="flex flex-col items-start w-full text-primary-100"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <h2 className="text-3xl font-semibold text-primary-100">Filter</h2>

        <div className="w-full flex flex-col items-start gap-y-[24px] mt-[24px]">
          <div className="w-full">
            <h3 className="font-semibold text-base mb-[10px]">Category</h3>

            <div className="flex flex-wrap gap-2.5 mt-1.5">
              {categories.map((category) => {
                return (
                  <FindTutorFilterItem
                    key={`category-${category.slug}`}
                    isActive={activeCategory.includes(category.id)}
                    onClick={() => handleSelectClassTypes(category.id)}
                  >
                    {category.name}
                  </FindTutorFilterItem>
                )
              })}
            </div>
          </div>

          <div className="flex flex-col items-start justify-start w-full">
            <h3 className="font-semibold text-base mb-[10px]">Tutor’s Language</h3>

            <Controller
              name="languages"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <DropDown
                    className="!mb-[6px]"
                    placeholder="Select Language"
                    menuItems={formattedLanguages}
                    value={value}
                    setValue={onChange}
                  />
                )
              }}
            />
          </div>

          <div className="w-full">
            <h3 className="font-semibold text-base mb-[10px]">Price</h3>

            <div className="flex flex-wrap gap-2.5 mt-1.5">
              {tutorPrices.map((tutorPrice) => {
                return (
                  <FindTutorFilterItem
                    key={`price-${tutorPrice.value}`}
                    isActive={price === tutorPrice.value}
                    onClick={() => handleChangePrice(tutorPrice.value)}
                  >
                    {tutorPrice.label}
                  </FindTutorFilterItem>
                )
              })}
            </div>
          </div>

          <div className="w-full">
            <h3 className="font-semibold text-base mb-[10px]">Tutor’s Rating</h3>

            <div className="flex flex-wrap gap-2.5 mt-1.5">
              {tutorRatings.map((tutorRating) => {
                return (
                  <FindTutorFilterItem
                    key={`rating-${tutorRating.value}`}
                    isActive={rating === tutorRating.value}
                    onClick={() => handleChangeRating(tutorRating.value)}
                  >
                    {tutorRating.label}
                  </FindTutorFilterItem>
                )
              })}
            </div>
          </div>

          <div className="flex flex-col items-start justify-start w-full">
            <h3 className="font-semibold text-base mb-[10px]">Class Level</h3>

            <Controller
              name="class_banner_level"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <DropDown
                    className="!mb-[6px]"
                    menuItems={tutorClassLevels}
                    value={value}
                    setValue={onChange}
                  />
                )
              }}
            />
          </div>

          <div className="flex flex-col items-start justify-start w-full">
            <h3 className="font-semibold text-base mb-[10px]">Timezone</h3>

            <Controller
              name="timezone"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <DropDown
                    className="!mb-[6px]"
                    placeholder="Select Timezone"
                    menuItems={formattedTimezones}
                    value={value}
                    setValue={onChange}
                  />
                )
              }}
            />
          </div>

          <div className="w-full">
            <h3 className="font-semibold text-base mb-[10px]">
              Time Preferences (choose according to your time zone)
            </h3>

            <div className="flex flex-wrap gap-2.5 mt-1.5">
              {suitableTimes.map((suitableTime) => {
                return (
                  <FindTutorFilterItem
                    key={`time-${suitableTime.value}`}
                    isActive={timePreferences.includes(suitableTime.value)}
                    onClick={() => handleSelectSuitableTimeSlot(suitableTime.value)}
                  >
                    {suitableTime.label}
                  </FindTutorFilterItem>
                )
              })}
            </div>
          </div>

          <div className="w-full">
            <h3 className="font-semibold text-base mb-[12px]">Tutor preferences</h3>
            <div className="flex itemc-center justify-between gap-x-[20px]">
              <div className="flex flex-col items-start justify-start flex-1">
                <span className="inline-block text-sm font-medium mb-[6px]">
                  Tutor’s origin country
                </span>

                <Controller
                  control={control}
                  name="country_of_origin"
                  render={({ field: { value, onChange } }) => {
                    return (
                      <DropDown
                        placeholder="Select Country of Origin"
                        menuItems={allFormattedCountries}
                        value={value}
                        setValue={onChange}
                      />
                    )
                  }}
                />
              </div>

              <div className="flex flex-col items-start justify-start flex-1">
                <span className="inline-block text-sm font-medium mb-[6px]">
                  Tutor’s residence country
                </span>

                <Controller
                  control={control}
                  name="country_of_residence"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <DropDown
                        placeholder="Select Country of Residence"
                        menuItems={allFormattedCountries}
                        value={value}
                        setValue={onChange}
                      />
                    )
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-[50px] flex items-center justify-center w-full gap-x-[10px]">
          <button
            type="button"
            className="flex-1 h-[44px] rounded-[8px] border-[#F67443] border-[1px] shadow-button-second flex items-center justify-center text-base font-semibold text-primary-100 hover:bg-[#F67443] hover:text-white transition-colors duration-200 ease"
            onClick={handleClose}
          >
            Cancel
          </button>

          <Button type="submit" isLoading={false} className="flex-1 font-semibold">
            Apply Filter
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

export default FindTutorFilterModal
